import { useCallback, useEffect, useMemo, useState } from 'react';
import { SiteAnalyticsSummary } from '../types';
import { mockBackend } from '../services/mockBackend';
import { userFacingError } from '../utils/errors';

const numberFormatter = new Intl.NumberFormat('pt-BR');
const decimalFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
});

const emptySummary: SiteAnalyticsSummary = {
  days: 30,
  totalViews: 0,
  uniqueVisitors: 0,
  viewsToday: 0,
  viewsLast7Days: 0,
  averageDailyViews: 0,
  topPages: [],
  topRegistrations: [],
  dailyViews: [],
  deviceBreakdown: [],
  browserBreakdown: [],
  recentViews: [],
};

const AdminAnalyticsPanel = () => {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<SiteAnalyticsSummary>(emptySummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setSummary(await mockBackend.analytics.getSiteAnalytics(days));
    } catch (loadError) {
      setError(userFacingError(loadError, 'Não foi possível carregar as métricas agora.'));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void loadAnalytics();
  }, [loadAnalytics]);

  const maxDailyViews = useMemo(
    () => Math.max(1, ...summary.dailyViews.map((item) => item.views)),
    [summary.dailyViews]
  );
  const maxPageViews = useMemo(
    () => Math.max(1, ...summary.topPages.map((item) => item.views)),
    [summary.topPages]
  );
  const maxRegistrationClicks = useMemo(
    () => Math.max(1, ...summary.topRegistrations.map((item) => item.clicks)),
    [summary.topRegistrations]
  );

  return (
    <div className="d-grid gap-4">
      <div className="card bg-onyx border-0 shadow-lg">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-4">
            <div>
              <h5 className="text-white mb-1">Acessos do site</h5>
              <p className="text-white-50 mb-0">
                Pageviews, visitantes recorrentes e páginas mais acessadas.
              </p>
            </div>
            <div className="d-flex gap-2">
              <select
                className="form-select input-contrast"
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
              >
                <option value={7}>7 dias</option>
                <option value={30}>30 dias</option>
                <option value={90}>90 dias</option>
              </select>
              <button type="button" className="btn btn-outline-light" onClick={loadAnalytics}>
                Atualizar
              </button>
            </div>
          </div>

          {error && <div className="alert alert-danger py-2">{error}</div>}

          {loading ? (
            <div className="text-white-50">Carregando métricas...</div>
          ) : (
            <>
              <div className="analytics-metric-grid mb-4">
                <div className="metric-card">
                  <span>Visualizações</span>
                  <strong>{numberFormatter.format(summary.totalViews)}</strong>
                </div>
                <div className="metric-card">
                  <span>Visitantes</span>
                  <strong>{numberFormatter.format(summary.uniqueVisitors)}</strong>
                </div>
                <div className="metric-card">
                  <span>Hoje</span>
                  <strong>{numberFormatter.format(summary.viewsToday)}</strong>
                </div>
                <div className="metric-card">
                  <span>Últimos 7 dias</span>
                  <strong>{numberFormatter.format(summary.viewsLast7Days)}</strong>
                </div>
                <div className="metric-card">
                  <span>Média diária</span>
                  <strong>{decimalFormatter.format(summary.averageDailyViews)}</strong>
                </div>
              </div>

              <div className="analytics-chart-panel mb-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="text-white fw-semibold">Visualizações por dia</div>
                  <span className="text-white-50 small">{summary.days} dias</span>
                </div>
                <div className="daily-access-chart">
                  {summary.dailyViews.map((item) => {
                    const height = Math.max(8, (item.views / maxDailyViews) * 100);
                    return (
                      <div className="daily-access-column" key={item.date}>
                        <div className="daily-access-bar-wrap">
                          <div className="daily-access-bar" style={{ height: `${height}%` }} />
                        </div>
                        <strong>{numberFormatter.format(item.views)}</strong>
                        <span>{dateFormatter.format(new Date(`${item.date}T00:00:00`))}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="row g-4">
                <div className="col-lg-7">
                  <div className="analytics-chart-panel h-100">
                    <div className="text-white fw-semibold mb-3">Páginas mais acessadas</div>
                    <div className="analytics-rank-list">
                      {summary.topPages.length === 0 ? (
                        <div className="empty-box">Sem acessos registrados no período.</div>
                      ) : (
                        summary.topPages.map((page) => (
                          <div className="analytics-rank-item" key={page.path}>
                            <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                              <strong>{page.path}</strong>
                              <span>{numberFormatter.format(page.views)}</span>
                            </div>
                            <div className="analytics-progress">
                              <div style={{ width: `${(page.views / maxPageViews) * 100}%` }} />
                            </div>
                            <small>{numberFormatter.format(page.uniqueVisitors)} visitantes</small>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-5">
                  <div className="analytics-chart-panel h-100">
                    <div className="text-white fw-semibold mb-3">Dispositivo e navegador</div>
                    <div className="analytics-breakdown-grid">
                      <div>
                        <span className="text-white-50 small d-block mb-2">Dispositivos</span>
                        <BreakdownList items={summary.deviceBreakdown} />
                      </div>
                      <div>
                        <span className="text-white-50 small d-block mb-2">Navegadores</span>
                        <BreakdownList items={summary.browserBreakdown} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="analytics-chart-panel mb-4">
                <div className="text-white fw-semibold mb-3">Inscrições mais clicadas</div>
                <div className="analytics-rank-list">
                  {summary.topRegistrations.length === 0 ? (
                    <div className="empty-box">Nenhum clique de inscrição registrado no período.</div>
                  ) : (
                    summary.topRegistrations.map((registration) => (
                      <div className="analytics-rank-item" key={registration.eventId}>
                        <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                          <strong>{registration.eventTitle}</strong>
                          <span>{numberFormatter.format(registration.clicks)}</span>
                        </div>
                        <div className="analytics-progress">
                          <div style={{ width: `${(registration.clicks / maxRegistrationClicks) * 100}%` }} />
                        </div>
                        <small>
                          {numberFormatter.format(registration.uniqueVisitors)} visitantes · {registration.registrationLink}
                        </small>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="analytics-chart-panel mt-4">
                <div className="text-white fw-semibold mb-3">Acessos recentes</div>
                <div className="analytics-recent-list">
                  {summary.recentViews.length === 0 ? (
                    <div className="empty-box">Nenhum acesso registrado ainda.</div>
                  ) : (
                    summary.recentViews.map((item, index) => (
                      <div className="analytics-recent-item" key={`${item.path}-${item.createdAt}-${index}`}>
                        <strong>{item.path}</strong>
                        <span>{item.device} · {item.browser}</span>
                        <small>{new Date(item.createdAt).toLocaleString('pt-BR')}</small>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

type BreakdownListProps = {
  items: SiteAnalyticsSummary['deviceBreakdown'];
};

const BreakdownList = ({ items }: BreakdownListProps) => {
  const max = Math.max(1, ...items.map((item) => item.count));

  if (items.length === 0) {
    return <div className="empty-box compact">Sem dados.</div>;
  }

  return (
    <div className="analytics-breakdown-list">
      {items.map((item) => (
        <div className="analytics-breakdown-item" key={item.label}>
          <div className="d-flex align-items-center justify-content-between gap-2">
            <strong>{item.label}</strong>
            <span>{numberFormatter.format(item.count)}</span>
          </div>
          <div className="analytics-progress">
            <div style={{ width: `${(item.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminAnalyticsPanel;
